import React, { useState, useEffect } from 'react';
import { Bot, BotConfig, AnalysisSettings } from '../types/api';
import TradingPairSelector from './TradingPairSelector';
import { Checkbox } from './ui/checkbox';

export interface BotFormData {
  name: string;
  testnet: boolean;
  trading_capital: number;
  max_risk_per_trade: number;
  coins: string[];
  signal_refresh_interval: number;
  news_refresh_interval: number;
  news_fetcher: boolean;
  llm_no_action: boolean;
  force_full_allocation: boolean;
  sync_existing_positions: boolean;
  telegram_notifications: boolean;
  // API Keys
  binance_testnet_api_key: string;
  binance_testnet_secret_key: string;
  binance_api_key: string;
  binance_secret_key: string;
  gemini_api_key: string;
  cryptopanic_api_key: string;
  telegram_bot_token: string;
  telegram_chat_id: string;
  // Notification Settings
  notification_interval_hours: number;
  notify_on_position_change: boolean;
  notify_on_trade: boolean;
  notify_on_error: boolean;
  include_detailed_metrics: boolean;
  // Trend Analyzer
  trend_threshold: number;
  volume_threshold: number;
  price_action_threshold: number;
  swing_window: number;
  divergence_periods: number;
  price_action_lookback: number;
  overall_confidence_threshold: number;
  // Technical Indicator Thresholds
  rsi_oversold: number;
  rsi_overbought: number;
  rsi_extreme_oversold: number;
  rsi_extreme_overbought: number;
  adx_strong_trend: number;
  adx_very_strong: number;
  adx_weak_trend: number;
  momentum_bullish: number;
  momentum_bearish: number;
  momentum_neutral_high: number;
  momentum_neutral_low: number;
  volatility_low: number;
  volatility_normal: number;
  volatility_high: number;
  volatility_extreme: number;
  macd_bullish_threshold: number;
  macd_bearish_threshold: number;
  // Analysis Settings
  enable_risk_analysis: boolean;
  enable_trend_analysis: boolean;
  enable_volume_analysis: boolean;
  // Auto-configuration settings
  auto_configure_risk: boolean;
  auto_configure_trend: boolean;
  auto_configure_volume: boolean;
  reconfigure_on_position_close: boolean;
  reconfigure_interval_hours: number;
  // PnL Threshold Settings
  pnl_enabled: boolean;
  pnl_profit_threshold: number;
  pnl_loss_threshold: number;
  pnl_cooldown_minutes: number;
}

export const defaultFormData: BotFormData = {
  name: '',
  testnet: true,
  trading_capital: 100,
  max_risk_per_trade: 0.02,
  coins: [],
  signal_refresh_interval: 120,
  news_refresh_interval: 500,
  news_fetcher: false,
  llm_no_action: true,
  force_full_allocation: false,
  sync_existing_positions: false,
  telegram_notifications: false,
  // API Keys
  binance_testnet_api_key: '',
  binance_testnet_secret_key: '',
  binance_api_key: '',
  binance_secret_key: '',
  gemini_api_key: '',
  cryptopanic_api_key: '',
  telegram_bot_token: '',
  telegram_chat_id: '',
  // Notification Settings
  notification_interval_hours: 1.0,
  notify_on_position_change: true,
  notify_on_trade: true,
  notify_on_error: true,
  include_detailed_metrics: true,
  // Trend Analyzer
  trend_threshold: 25,
  volume_threshold: 0.6,
  price_action_threshold: 0.49,
  swing_window: 5,
  divergence_periods: 5,
  price_action_lookback: 10,
  overall_confidence_threshold: 0.5,
  // Technical Indicator Thresholds
  rsi_oversold: 30,
  rsi_overbought: 70,
  rsi_extreme_oversold: 20,
  rsi_extreme_overbought: 80,
  adx_strong_trend: 25,
  adx_very_strong: 50,
  adx_weak_trend: 20,
  momentum_bullish: 70,
  momentum_bearish: 30,
  momentum_neutral_high: 60,
  momentum_neutral_low: 40,
  volatility_low: 1.0,
  volatility_normal: 2.0,
  volatility_high: 4.0,
  volatility_extreme: 8.0,
  macd_bullish_threshold: 0.0001,
  macd_bearish_threshold: -0.0001,
  // Analysis Settings
  enable_risk_analysis: true,
  enable_trend_analysis: true,
  enable_volume_analysis: true,
  // Auto-configuration settings
  auto_configure_risk: false,
  auto_configure_trend: false,
  auto_configure_volume: false,
  reconfigure_on_position_close: true,
  reconfigure_interval_hours: 24.0,
  // PnL Threshold Settings
  pnl_enabled: false,
  pnl_profit_threshold: 5.0,
  pnl_loss_threshold: 3.0,
  pnl_cooldown_minutes: 15,
};

export const botToFormData = (bot: Bot): BotFormData => {
  const pnlSettings = bot.pnl_threshold_settings;
  
  return {
    name: bot.name,
    testnet: bot.config.testnet,
    trading_capital: bot.config.trading_capital,
    max_risk_per_trade: bot.config.max_risk_per_trade,
    coins: bot.config.coins,
    signal_refresh_interval: bot.config.signal_refresh_interval,
    news_refresh_interval: bot.config.news_refresh_interval,
    news_fetcher: bot.config.news_fetcher,
    llm_no_action: bot.config.llm_no_action,
    force_full_allocation: bot.config.force_full_allocation || false,
    sync_existing_positions: bot.config.sync_existing_positions !== false,
    telegram_notifications: !!(bot.notification_settings?.enabled && bot.notification_settings?.telegram_bot_token && bot.notification_settings?.telegram_chat_id),
    // API Keys
    binance_testnet_api_key: bot.config.api_keys.binance_testnet_api_key || '',
    binance_testnet_secret_key: bot.config.api_keys.binance_testnet_secret_key || '',
    binance_api_key: bot.config.api_keys.binance_api_key || '',
    binance_secret_key: bot.config.api_keys.binance_secret_key || '',
    gemini_api_key: bot.config.api_keys.gemini_api_key || '',
    cryptopanic_api_key: bot.config.api_keys.cryptopanic_api_key || '',
    telegram_bot_token: bot.notification_settings?.telegram_bot_token || bot.config.api_keys.telegram_bot_token || '',
    telegram_chat_id: bot.notification_settings?.telegram_chat_id || bot.config.api_keys.telegram_chat_id || '',
    // Trend Analyzer
    trend_threshold: bot.config.trend_analyzer?.trend_threshold || 25,
    volume_threshold: bot.config.trend_analyzer?.volume_threshold || 0.6,
    price_action_threshold: bot.config.trend_analyzer?.price_action_threshold || 0.49,
    swing_window: bot.config.trend_analyzer?.swing_window || 5,
    divergence_periods: bot.config.trend_analyzer?.divergence_periods || 5,
    price_action_lookback: bot.config.trend_analyzer?.price_action_lookback || 10,
    overall_confidence_threshold: bot.config.trend_analyzer?.overall_confidence_threshold || 0.5,
    // Technical Indicator Thresholds
    rsi_oversold: bot.config.technical_thresholds?.rsi_oversold || 30,
    rsi_overbought: bot.config.technical_thresholds?.rsi_overbought || 70,
    rsi_extreme_oversold: bot.config.technical_thresholds?.rsi_extreme_oversold || 20,
    rsi_extreme_overbought: bot.config.technical_thresholds?.rsi_extreme_overbought || 80,
    adx_strong_trend: bot.config.technical_thresholds?.adx_strong_trend || 25,
    adx_very_strong: bot.config.technical_thresholds?.adx_very_strong || 50,
    adx_weak_trend: bot.config.technical_thresholds?.adx_weak_trend || 20,
    momentum_bullish: bot.config.technical_thresholds?.momentum_bullish || 70,
    momentum_bearish: bot.config.technical_thresholds?.momentum_bearish || 30,
    momentum_neutral_high: bot.config.technical_thresholds?.momentum_neutral_high || 60,
    momentum_neutral_low: bot.config.technical_thresholds?.momentum_neutral_low || 40,
    volatility_low: bot.config.technical_thresholds?.volatility_low || 1.0,
    volatility_normal: bot.config.technical_thresholds?.volatility_normal || 2.0,
    volatility_high: bot.config.technical_thresholds?.volatility_high || 4.0,
    volatility_extreme: bot.config.technical_thresholds?.volatility_extreme || 8.0,
    macd_bullish_threshold: bot.config.technical_thresholds?.macd_bullish_threshold || 0.0001,
    macd_bearish_threshold: bot.config.technical_thresholds?.macd_bearish_threshold || -0.0001,
    // Notification Settings
    notification_interval_hours: bot.notification_settings?.notification_interval_hours || 1.0,
    notify_on_position_change: bot.notification_settings?.notify_on_position_change ?? true,
    notify_on_trade: bot.notification_settings?.notify_on_trade ?? true,
    notify_on_error: bot.notification_settings?.notify_on_error ?? true,
    include_detailed_metrics: bot.notification_settings?.include_detailed_metrics ?? true,
    // Analysis Settings
    enable_risk_analysis: bot.analysis_settings?.enable_risk_analysis ?? true,
    enable_trend_analysis: bot.analysis_settings?.enable_trend_analysis ?? true,
    enable_volume_analysis: bot.analysis_settings?.enable_volume_analysis ?? true,
    auto_configure_risk: bot.analysis_settings?.auto_configure_risk ?? false,
    auto_configure_trend: bot.analysis_settings?.auto_configure_trend ?? false,
    auto_configure_volume: bot.analysis_settings?.auto_configure_volume ?? false,
    reconfigure_on_position_close: bot.analysis_settings?.reconfigure_on_position_close ?? true,
    reconfigure_interval_hours: bot.analysis_settings?.reconfigure_interval_hours ?? 24.0,
    // PnL Threshold Settings from bot.pnl_threshold_settings
    pnl_enabled: pnlSettings?.enabled || false,
    pnl_profit_threshold: pnlSettings?.profit_threshold_pct || 5.0,
    pnl_loss_threshold: pnlSettings?.loss_threshold_pct || 3.0,
    pnl_cooldown_minutes: pnlSettings?.cooldown_minutes || 15,
  };
};

export const formDataToPayload = (formData: BotFormData) => {
  const config: BotConfig = {
    api_keys: {
      binance_testnet_api_key: formData.binance_testnet_api_key || undefined,
      binance_testnet_secret_key: formData.binance_testnet_secret_key || undefined,
      binance_api_key: formData.binance_api_key || undefined,
      binance_secret_key: formData.binance_secret_key || undefined,
      gemini_api_key: formData.gemini_api_key || undefined,
      cryptopanic_api_key: formData.cryptopanic_api_key || undefined,
      telegram_bot_token: formData.telegram_bot_token || undefined,
      telegram_chat_id: formData.telegram_chat_id || undefined,
    },
    testnet: formData.testnet,
    trading_capital: formData.trading_capital,
    max_risk_per_trade: formData.max_risk_per_trade,
    coins: formData.coins,
    signal_refresh_interval: formData.signal_refresh_interval,
    news_refresh_interval: formData.news_refresh_interval,
    news_fetcher: formData.news_fetcher,
    llm_no_action: formData.llm_no_action,
    force_full_allocation: formData.force_full_allocation,
    sync_existing_positions: formData.sync_existing_positions,
    trend_analyzer: {
      trend_threshold: formData.trend_threshold,
      volume_threshold: formData.volume_threshold,
      price_action_threshold: formData.price_action_threshold,
      swing_window: formData.swing_window,
      divergence_periods: formData.divergence_periods,
      price_action_lookback: formData.price_action_lookback,
      overall_confidence_threshold: formData.overall_confidence_threshold,
    },
    technical_thresholds: {
      rsi_oversold: formData.rsi_oversold,
      rsi_overbought: formData.rsi_overbought,
      rsi_extreme_oversold: formData.rsi_extreme_oversold,
      rsi_extreme_overbought: formData.rsi_extreme_overbought,
      adx_strong_trend: formData.adx_strong_trend,
      adx_very_strong: formData.adx_very_strong,
      adx_weak_trend: formData.adx_weak_trend,
      momentum_bullish: formData.momentum_bullish,
      momentum_bearish: formData.momentum_bearish,
      momentum_neutral_high: formData.momentum_neutral_high,
      momentum_neutral_low: formData.momentum_neutral_low,
      volatility_low: formData.volatility_low,
      volatility_normal: formData.volatility_normal,
      volatility_high: formData.volatility_high,
      volatility_extreme: formData.volatility_extreme,
      macd_bullish_threshold: formData.macd_bullish_threshold,
      macd_bearish_threshold: formData.macd_bearish_threshold,
    }
  };

  const notification_settings = formData.telegram_notifications ? {
    enabled: true,
    telegram_bot_token: formData.telegram_bot_token,
    telegram_chat_id: formData.telegram_chat_id,
    notification_interval_hours: formData.notification_interval_hours,
    notify_on_position_change: formData.notify_on_position_change,
    notify_on_trade: formData.notify_on_trade,
    notify_on_error: formData.notify_on_error,
    include_detailed_metrics: formData.include_detailed_metrics,
  } : {
    enabled: false,
    telegram_bot_token: undefined,
    telegram_chat_id: undefined,
    notification_interval_hours: 1.0,
    notify_on_position_change: true,
    notify_on_trade: true,
    notify_on_error: true,
    include_detailed_metrics: true,
  };

  const analysis_settings: AnalysisSettings = {
    enable_risk_analysis: formData.enable_risk_analysis,
    enable_trend_analysis: formData.enable_trend_analysis,
    enable_volume_analysis: formData.enable_volume_analysis,
    auto_configure_risk: formData.auto_configure_risk,
    auto_configure_trend: formData.auto_configure_trend,
    auto_configure_volume: formData.auto_configure_volume,
    reconfigure_on_position_close: formData.reconfigure_on_position_close,
    reconfigure_interval_hours: formData.reconfigure_interval_hours,
  };
  
  const pnl_threshold_settings = {
    enabled: formData.pnl_enabled,
    profit_threshold_pct: Number(formData.pnl_profit_threshold) || 5.0,
    loss_threshold_pct: Number(formData.pnl_loss_threshold) || 3.0,
    cooldown_minutes: Number(formData.pnl_cooldown_minutes) || 15,
  };

  return {
    name: formData.name,
    config,
    notification_settings,
    analysis_settings,
    pnl_threshold_settings
  };
};

interface BotFormModalProps {
  title: string;
  submitText: string;
  loadingText: string;
  formData: BotFormData;
  setFormData: React.Dispatch<React.SetStateAction<BotFormData>>;
  onSubmit: (e: React.FormEvent) => Promise<void>;
  onClose: () => void;
  loading: boolean;
  error: string;
  isEdit?: boolean;
}

const BotFormModal: React.FC<BotFormModalProps> = ({
  title,
  submitText,
  loadingText,
  formData,
  setFormData,
  onSubmit,
  onClose,
  loading,
  error,
  isEdit = false
}) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showTechnicalThresholds, setShowTechnicalThresholds] = useState(false);
  const [showTrendAnalyzer, setShowTrendAnalyzer] = useState(false);
  const [showAnalysisSettings, setShowAnalysisSettings] = useState(false);
  const [showApiKeys, setShowApiKeys] = useState(false);
  const [showPnLThresholds, setShowPnLThresholds] = useState(false);
  const [showNotificationSettings, setShowNotificationSettings] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    let processedValue: any = value;
    if (type === 'checkbox') {
      processedValue = checked;
    } else if (type === 'number') {
      processedValue = value === '' ? '' : parseFloat(value) || 0;
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
  };

  const handleTradingPairsChange = (pairs: string[]) => {
    setFormData(prev => ({
      ...prev,
      coins: pairs
    }));
  };

  // Generate unique IDs for form elements based on whether it's edit or create
  const idPrefix = isEdit ? 'edit-' : 'create-';

  return (
    <div className="fixed inset-0 bg-black/50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border border-border w-11/12 max-w-4xl shadow-lg rounded-md bg-background">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-foreground">{title}</h3>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={onSubmit} className="space-y-6">
            {/* Basic Configuration */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-foreground">Bot Name</label>
                <input
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border-input bg-background text-foreground rounded-md shadow-sm focus:ring-primary focus:border-primary"
                  placeholder="My Trading Bot"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground">Trading Capital ($)</label>
                <input
                  type="number"
                  name="trading_capital"
                  step="0.01"
                  min="0"
                  value={formData.trading_capital}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border-input bg-background text-foreground rounded-md shadow-sm focus:ring-primary focus:border-primary"
                />
              </div>

              <div className="md:col-span-2">
                <TradingPairSelector
                  selectedPairs={formData.coins}
                  onSelectionChange={handleTradingPairsChange}
                  testnet={formData.testnet}
                />
                <p className="mt-1 text-sm text-muted-foreground">Select trading pairs for the bot to monitor and trade</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground">Max Risk Per Trade (%)</label>
                <input
                  type="number"
                  name="max_risk_per_trade"
                  step="0.001"
                  min="0"
                  max="1"
                  value={formData.max_risk_per_trade}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border-input bg-background text-foreground rounded-md shadow-sm focus:ring-primary focus:border-primary"
                />
              </div>

              {isEdit && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-foreground">Signal Refresh Interval (seconds)</label>
                    <input
                      type="number"
                      name="signal_refresh_interval"
                      min="10"
                      value={formData.signal_refresh_interval}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border-input bg-background text-foreground rounded-md shadow-sm focus:ring-primary focus:border-primary"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground">News Refresh Interval (seconds)</label>
                    <input
                      type="number"
                      name="news_refresh_interval"
                      min="60"
                      value={formData.news_refresh_interval}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border-input bg-background text-foreground rounded-md shadow-sm focus:ring-primary focus:border-primary"
                    />
                  </div>
                </>
              )}
            </div>

            {/* Checkboxes */}
            <div className="space-y-4">
              <div className="flex items-center">
                <Checkbox
                  id={`${idPrefix}testnet`}
                  checked={formData.testnet}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, testnet: !!checked }))}
                />
                <label htmlFor={`${idPrefix}testnet`} className="ml-2 block text-sm text-foreground cursor-pointer">
                  Use Testnet (Recommended for testing)
                </label>
              </div>

              <div className="flex items-center">
                <Checkbox
                  id={`${idPrefix}llm_no_action`}
                  checked={formData.llm_no_action}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, llm_no_action: !!checked }))}
                />
                <label htmlFor={`${idPrefix}llm_no_action`} className="ml-2 block text-sm text-foreground cursor-pointer">
                  Safe Mode (LLM analysis only, no actual trades)
                </label>
              </div>

              <div className="flex items-center">
                <Checkbox
                  id={`${idPrefix}news_fetcher`}
                  checked={formData.news_fetcher}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, news_fetcher: !!checked }))}
                />
                <label htmlFor={`${idPrefix}news_fetcher`} className="ml-2 block text-sm text-foreground cursor-pointer">
                  Enable News Fetching
                </label>
              </div>

              <div className="flex items-center">
                <Checkbox
                  id={`${idPrefix}force_full_allocation`}
                  checked={formData.force_full_allocation}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, force_full_allocation: !!checked }))}
                />
                <label htmlFor={`${idPrefix}force_full_allocation`} className="ml-2 block text-sm text-foreground cursor-pointer">
                  Force Full Capital Allocation (Always invest 100% of set capital)
                </label>
              </div>

              <div className="flex items-center">
                <Checkbox
                  id={`${idPrefix}sync_existing_positions`}
                  checked={formData.sync_existing_positions}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, sync_existing_positions: !!checked }))}
                />
                <label htmlFor={`${idPrefix}sync_existing_positions`} className="ml-2 block text-sm text-foreground cursor-pointer">
                  Sync Existing Positions on Startup (Load open positions from exchange)
                </label>
              </div>

              <div className="flex items-center">
                <Checkbox
                  id={`${idPrefix}telegram_notifications`}
                  checked={formData.telegram_notifications}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, telegram_notifications: !!checked }))}
                />
                <label htmlFor={`${idPrefix}telegram_notifications`} className="ml-2 block text-sm text-foreground cursor-pointer">
                  Enable Telegram Notifications
                </label>
              </div>
            </div>

            {/* Advanced Settings */}
            <div className="border-t pt-6">
              <button
                type="button"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className={`flex items-center justify-between w-full text-left mb-6 p-3 ${isEdit ? 'bg-muted/50 hover:bg-muted' : 'bg-muted hover:bg-muted/80'} rounded-lg transition-colors`}
              >
                <h4 className="text-md font-medium text-foreground flex items-center">
                  <svg className={`w-5 h-5 mr-2 transition-transform ${showAdvanced ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  Advanced Settings
                </h4>
                <span className="text-sm text-muted-foreground">
                  {showAdvanced ? 'Hide' : 'Show'} advanced configuration
                </span>
              </button>
              
              {showAdvanced && (
              <div>
              
              {/* Notification Settings */}
              {formData.telegram_notifications && (
                <div className="mb-8 border border-border rounded-lg">
                  <button
                    type="button"
                    onClick={() => setShowNotificationSettings(!showNotificationSettings)}
                    className="flex items-center justify-between w-full text-left p-4 bg-muted/50 hover:bg-muted rounded-t-lg transition-colors"
                  >
                    <h5 className="text-sm font-semibold text-foreground flex items-center">
                      <svg className={`w-4 h-4 mr-2 transition-transform ${showNotificationSettings ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                      <span className="w-3 h-3 bg-purple-500 rounded-full mr-2"></span>
                      Notification Settings
                    </h5>
                    <span className="text-xs text-muted-foreground">
                      {showNotificationSettings ? 'Hide' : 'Show'} notification preferences
                    </span>
                  </button>
                  
                  {showNotificationSettings && (
                  <div className="p-4 border-t border-border">
                    <div className="space-y-6">
                      {/* Notification Preferences */}
                      <div className="bg-muted/30 border border-border rounded-lg p-4">
                        <h6 className="text-sm font-medium text-foreground mb-3 flex items-center">
                          <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                          ⚙️ Notification Preferences
                        </h6>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                          <div>
                            <label className="block text-sm font-medium text-foreground mb-2">
                              ⏰ Notification Interval (hours)
                            </label>
                            <input
                              type="number"
                              name="notification_interval_hours"
                              min="0.1"
                              max="24"
                              step="0.1"
                              value={formData.notification_interval_hours}
                              onChange={handleInputChange}
                              className="block w-full border-input bg-background text-foreground rounded-md shadow-sm focus:ring-primary focus:border-primary"
                            />
                            <p className="mt-1 text-xs text-muted-foreground">
                              How often to send detailed status updates (default: 1 hour)
                            </p>
                          </div>
                        </div>

                        <div className="space-y-3">
                          <div className="flex items-center">
                            <Checkbox
                              id={`${idPrefix}notify_on_position_change`}
                              checked={formData.notify_on_position_change}
                              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, notify_on_position_change: !!checked }))}
                            />
                            <label htmlFor={`${idPrefix}notify_on_position_change`} className="ml-2 block text-sm text-foreground cursor-pointer">
                              🎯 Notify on position changes (open/close positions)
                            </label>
                          </div>

                          <div className="flex items-center">
                            <Checkbox
                              id={`${idPrefix}notify_on_trade`}
                              checked={formData.notify_on_trade}
                              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, notify_on_trade: !!checked }))}
                            />
                            <label htmlFor={`${idPrefix}notify_on_trade`} className="ml-2 block text-sm text-foreground cursor-pointer">
                              💰 Notify on trade actions (buy/sell orders)
                            </label>
                          </div>

                          <div className="flex items-center">
                            <Checkbox
                              id={`${idPrefix}notify_on_error`}
                              checked={formData.notify_on_error}
                              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, notify_on_error: !!checked }))}
                            />
                            <label htmlFor={`${idPrefix}notify_on_error`} className="ml-2 block text-sm text-foreground cursor-pointer">
                              ⚠️ Notify on errors and system issues
                            </label>
                          </div>

                          <div className="flex items-center">
                            <Checkbox
                              id={`${idPrefix}include_detailed_metrics`}
                              checked={formData.include_detailed_metrics}
                              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, include_detailed_metrics: !!checked }))}
                            />
                            <label htmlFor={`${idPrefix}include_detailed_metrics`} className="ml-2 block text-sm text-foreground cursor-pointer">
                              📈 Include detailed portfolio metrics and LLM decisions
                            </label>
                          </div>
                        </div>

                        <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                          <div className="flex items-start">
                            <div className="flex-shrink-0">
                              <svg className="h-4 w-4 text-blue-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </div>
                            <div className="ml-2">
                              <p className="text-xs text-blue-700 dark:text-blue-300">
                                <strong>Enhanced notifications include:</strong> Real-time P&L tracking, position details, entry vs current prices, 
                                stop loss/take profit levels, latest AI decisions, and comprehensive portfolio overview.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  )}
                </div>
              )}
              
              {/* Technical Indicator Thresholds */}
              <div className={`mb-8 border ${isEdit ? 'border-border' : 'border-gray-200'} rounded-lg`}>
                <button
                  type="button"
                  onClick={() => setShowTechnicalThresholds(!showTechnicalThresholds)}
                  className={`flex items-center justify-between w-full text-left p-4 ${isEdit ? 'bg-muted/50 hover:bg-muted' : 'bg-muted hover:bg-muted/80'} rounded-t-lg transition-colors`}
                >
                  <h5 className="text-sm font-semibold text-foreground flex items-center">
                    <svg className={`w-4 h-4 mr-2 transition-transform ${showTechnicalThresholds ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    <span className="w-3 h-3 bg-blue-500 rounded-full mr-2"></span>
                    Technical Indicator Thresholds
                  </h5>
                  <span className="text-xs text-muted-foreground">
                    {showTechnicalThresholds ? 'Hide' : 'Show'} RSI, ADX, MACD, Momentum settings
                  </span>
                </button>
                
                {showTechnicalThresholds && (
                  <div className="p-4 border-t border-border">
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* RSI Thresholds */}
                  <div className={`${isEdit ? 'bg-muted/30' : 'bg-muted'} border border-border rounded-lg p-4`}>
                    <h6 className="font-medium text-foreground mb-3 flex items-center">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                      RSI (Relative Strength Index)
                    </h6>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-foreground mb-1">Extreme Oversold (&lt;)</label>
                        <input
                          type="number"
                          name="rsi_extreme_oversold"
                          min="0"
                          max="100"
                          step="0.1"
                          value={formData.rsi_extreme_oversold}
                          onChange={handleInputChange}
                          className="w-full px-2 py-1 text-sm border border-input bg-background text-foreground rounded focus:ring-1 focus:ring-primary focus:border-primary"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-foreground mb-1">Oversold (&lt;)</label>
                        <input
                          type="number"
                          name="rsi_oversold"
                          min="0"
                          max="100"
                          step="0.1"
                          value={formData.rsi_oversold}
                          onChange={handleInputChange}
                          className="w-full px-2 py-1 text-sm border border-input bg-background text-foreground rounded focus:ring-1 focus:ring-primary focus:border-primary"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-foreground mb-1">Overbought (&gt;)</label>
                        <input
                          type="number"
                          name="rsi_overbought"
                          min="0"
                          max="100"
                          step="0.1"
                          value={formData.rsi_overbought}
                          onChange={handleInputChange}
                          className="w-full px-2 py-1 text-sm border border-input bg-background text-foreground rounded focus:ring-1 focus:ring-primary focus:border-primary"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-foreground mb-1">Extreme Overbought (&gt;)</label>
                        <input
                          type="number"
                          name="rsi_extreme_overbought"
                          min="0"
                          max="100"
                          step="0.1"
                          value={formData.rsi_extreme_overbought}
                          onChange={handleInputChange}
                          className="w-full px-2 py-1 text-sm border border-input bg-background text-foreground rounded focus:ring-1 focus:ring-primary focus:border-primary"
                        />
                      </div>
                    </div>
                  </div>

                  {/* ADX Thresholds */}
                  <div className={`${isEdit ? 'bg-muted/30' : 'bg-muted'} border border-border rounded-lg p-4`}>
                    <h6 className="font-medium text-foreground mb-3 flex items-center">
                      <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                      ADX (Average Directional Index)
                    </h6>
                    <div className="grid grid-cols-1 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-foreground mb-1">Weak Trend (&lt;)</label>
                        <input
                          type="number"
                          name="adx_weak_trend"
                          min="0"
                          max="100"
                          step="0.1"
                          value={formData.adx_weak_trend}
                          onChange={handleInputChange}
                          className="w-full px-2 py-1 text-sm border border-input bg-background text-foreground rounded focus:ring-1 focus:ring-primary focus:border-primary"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-foreground mb-1">Strong Trend (&ge;)</label>
                        <input
                          type="number"
                          name="adx_strong_trend"
                          min="0"
                          max="100"
                          step="0.1"
                          value={formData.adx_strong_trend}
                          onChange={handleInputChange}
                          className="w-full px-2 py-1 text-sm border border-input bg-background text-foreground rounded focus:ring-1 focus:ring-primary focus:border-primary"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-foreground mb-1">Very Strong Trend (&ge;)</label>
                        <input
                          type="number"
                          name="adx_very_strong"
                          min="0"
                          max="100"
                          step="0.1"
                          value={formData.adx_very_strong}
                          onChange={handleInputChange}
                          className="w-full px-2 py-1 text-sm border border-input bg-background text-foreground rounded focus:ring-1 focus:ring-primary focus:border-primary"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Momentum Thresholds */}
                  <div className={`${isEdit ? 'bg-muted/30' : 'bg-muted'} border border-border rounded-lg p-4`}>
                    <h6 className="font-medium text-foreground mb-3 flex items-center">
                      <span className="w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
                      Momentum Score
                    </h6>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-foreground mb-1">Bearish (&lt;)</label>
                        <input
                          type="number"
                          name="momentum_bearish"
                          min="0"
                          max="100"
                          step="0.1"
                          value={formData.momentum_bearish}
                          onChange={handleInputChange}
                          className="w-full px-2 py-1 text-sm border border-input bg-background text-foreground rounded focus:ring-1 focus:ring-primary focus:border-primary"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-foreground mb-1">Neutral Low (&ge;)</label>
                        <input
                          type="number"
                          name="momentum_neutral_low"
                          min="0"
                          max="100"
                          step="0.1"
                          value={formData.momentum_neutral_low}
                          onChange={handleInputChange}
                          className="w-full px-2 py-1 text-sm border border-input bg-background text-foreground rounded focus:ring-1 focus:ring-primary focus:border-primary"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-foreground mb-1">Neutral High (&ge;)</label>
                        <input
                          type="number"
                          name="momentum_neutral_high"
                          min="0"
                          max="100"
                          step="0.1"
                          value={formData.momentum_neutral_high}
                          onChange={handleInputChange}
                          className="w-full px-2 py-1 text-sm border border-input bg-background text-foreground rounded focus:ring-1 focus:ring-primary focus:border-primary"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-foreground mb-1">Bullish (&ge;)</label>
                        <input
                          type="number"
                          name="momentum_bullish"
                          min="0"
                          max="100"
                          step="0.1"
                          value={formData.momentum_bullish}
                          onChange={handleInputChange}
                          className="w-full px-2 py-1 text-sm border border-input bg-background text-foreground rounded focus:ring-1 focus:ring-primary focus:border-primary"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Volatility Thresholds */}
                  <div className={`${isEdit ? 'bg-muted/30' : 'bg-muted'} border border-border rounded-lg p-4`}>
                    <h6 className="font-medium text-foreground mb-3 flex items-center">
                      <span className="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
                      Volatility (%)
                    </h6>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-foreground mb-1">Low (&ge;)</label>
                        <input
                          type="number"
                          name="volatility_low"
                          min="0"
                          step="0.1"
                          value={formData.volatility_low}
                          onChange={handleInputChange}
                          className="w-full px-2 py-1 text-sm border border-input bg-background text-foreground rounded focus:ring-1 focus:ring-primary focus:border-primary"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-foreground mb-1">Normal (&ge;)</label>
                        <input
                          type="number"
                          name="volatility_normal"
                          min="0"
                          step="0.1"
                          value={formData.volatility_normal}
                          onChange={handleInputChange}
                          className="w-full px-2 py-1 text-sm border border-input bg-background text-foreground rounded focus:ring-1 focus:ring-primary focus:border-primary"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-foreground mb-1">High (&ge;)</label>
                        <input
                          type="number"
                          name="volatility_high"
                          min="0"
                          step="0.1"
                          value={formData.volatility_high}
                          onChange={handleInputChange}
                          className="w-full px-2 py-1 text-sm border border-input bg-background text-foreground rounded focus:ring-1 focus:ring-primary focus:border-primary"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-foreground mb-1">Extreme (&ge;)</label>
                        <input
                          type="number"
                          name="volatility_extreme"
                          min="0"
                          step="0.1"
                          value={formData.volatility_extreme}
                          onChange={handleInputChange}
                          className="w-full px-2 py-1 text-sm border border-input bg-background text-foreground rounded focus:ring-1 focus:ring-primary focus:border-primary"
                        />
                      </div>
                    </div>
                  </div>

                  {/* MACD Thresholds */}
                  <div className={`${isEdit ? 'bg-muted/30' : 'bg-muted'} border border-border rounded-lg p-4`}>
                    <h6 className="font-medium text-foreground mb-3 flex items-center">
                      <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                      MACD
                    </h6>
                    <div className="grid grid-cols-1 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-foreground mb-1">Strong Bearish (&le;)</label>
                        <input
                          type="number"
                          name="macd_bearish_threshold"
                          step="0.0001"
                          value={formData.macd_bearish_threshold}
                          onChange={handleInputChange}
                          className="w-full px-2 py-1 text-sm border border-input bg-background text-foreground rounded focus:ring-1 focus:ring-primary focus:border-primary"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-foreground mb-1">Strong Bullish (&ge;)</label>
                        <input
                          type="number"
                          name="macd_bullish_threshold"
                          step="0.0001"
                          value={formData.macd_bullish_threshold}
                          onChange={handleInputChange}
                          className="w-full px-2 py-1 text-sm border border-input bg-background text-foreground rounded focus:ring-1 focus:ring-primary focus:border-primary"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                  </div>
                )}
              </div>
              
              {/* PnL Threshold Settings */}
              {!isEdit ? (
                <div className="mb-6 border border-border rounded-lg">
                  <button
                    type="button"
                    onClick={() => setShowPnLThresholds(!showPnLThresholds)}
                    className="flex items-center justify-between w-full text-left p-4 bg-muted hover:bg-muted/80 rounded-t-lg transition-colors"
                  >
                    <h5 className="text-sm font-semibold text-foreground flex items-center">
                      <svg className={`w-4 h-4 mr-2 transition-transform ${showPnLThresholds ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                      <span className="w-3 h-3 bg-yellow-500 rounded-full mr-2"></span>
                      PnL Threshold Settings
                    </h5>
                    <span className="text-xs text-muted-foreground">
                      {showPnLThresholds ? 'Hide' : 'Show'} profit/loss monitoring thresholds
                    </span>
                  </button>
                  
                  {showPnLThresholds && (
                    <div className="p-4 border-t border-border">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* PnL Monitoring Settings */}
                        <div className="bg-muted border border-border rounded-lg p-4">
                          <h6 className="font-medium text-foreground mb-3 flex items-center">
                            <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                            Profit Monitoring
                          </h6>
                          <div className="space-y-3">
                            <div>
                              <div className="flex items-center">
                                <Checkbox
                                  id="pnl_enabled"
                                  checked={formData.pnl_enabled}
                                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, pnl_enabled: !!checked }))}
                                />
                                <label htmlFor="pnl_enabled" className="ml-2 block text-sm font-medium text-foreground cursor-pointer">
                                  Enable PnL Monitoring
                                </label>
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">Monitor PnL changes and trigger LLM consultation when thresholds are exceeded</p>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-foreground mb-1">Profit Threshold (%)</label>
                              <input
                                type="number"
                                name="pnl_profit_threshold"
                                min="0.1"
                                max="20"
                                step="0.1"
                                value={formData.pnl_profit_threshold}
                                onChange={handleInputChange}
                                disabled={!formData.pnl_enabled}
                                className="w-full px-2 py-1 text-sm border border-input bg-background text-foreground rounded focus:ring-1 focus:ring-primary focus:border-primary disabled:opacity-50"
                              />
                              <p className="text-xs text-muted-foreground mt-1">Trigger consultation when profit changes by this % (min: 0.1%, default: 5%)</p>
                            </div>
                          </div>
                        </div>
                        
                        {/* Loss Monitoring Settings */}
                        <div className="bg-muted border border-border rounded-lg p-4">
                          <h6 className="font-medium text-foreground mb-3 flex items-center">
                            <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                            Loss Monitoring
                          </h6>
                          <div className="space-y-3">
                            <div>
                              <label className="block text-xs font-medium text-foreground mb-1">Loss Threshold (%)</label>
                              <input
                                type="number"
                                name="pnl_loss_threshold"
                                min="0.1"
                                max="20"
                                step="0.1"
                                value={formData.pnl_loss_threshold}
                                onChange={handleInputChange}
                                disabled={!formData.pnl_enabled}
                                className="w-full px-2 py-1 text-sm border border-input bg-background text-foreground rounded focus:ring-1 focus:ring-primary focus:border-primary disabled:opacity-50"
                              />
                              <p className="text-xs text-muted-foreground mt-1">Trigger consultation when loss changes by this % (min: 0.1%, default: 3%)</p>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-foreground mb-1">Cooldown Period (minutes)</label>
                              <input
                                type="number"
                                name="pnl_cooldown_minutes"
                                min="5"
                                max="60"
                                step="5"
                                value={formData.pnl_cooldown_minutes}
                                onChange={handleInputChange}
                                disabled={!formData.pnl_enabled}
                                className="w-full px-2 py-1 text-sm border border-input bg-background text-foreground rounded focus:ring-1 focus:ring-primary focus:border-primary disabled:opacity-50"
                              />
                              <p className="text-xs text-muted-foreground mt-1">Minimum time between PnL-triggered consultations (default: 15 min)</p>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Info Box */}
                      <div className="mt-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                        <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">How PnL Thresholds Work</h3>
                        <ul className="mt-2 text-xs text-blue-700 dark:text-blue-300 space-y-1">
                          <li>• When position PnL changes by the specified threshold, the LLM is consulted</li>
                          <li>• This happens even if market conditions haven't changed significantly</li>
                          <li>• The LLM will receive context about the PnL change and provide guidance</li>
                          <li>• Cooldown prevents excessive consultations during volatile periods</li>
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="mb-6 border border-border rounded-lg">
                  <button
                    type="button"
                    onClick={() => setShowPnLThresholds(!showPnLThresholds)}
                    className="flex items-center justify-between w-full text-left p-4 bg-muted/50 hover:bg-muted rounded-t-lg transition-colors"
                  >
                    <h5 className="text-sm font-semibold text-foreground flex items-center">
                      <svg className={`w-4 h-4 mr-2 transition-transform ${showPnLThresholds ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                      <span className="w-3 h-3 bg-yellow-500 rounded-full mr-2"></span>
                      PnL Threshold Settings
                    </h5>
                    <span className="text-xs text-muted-foreground">
                      {showPnLThresholds ? 'Hide' : 'Show'} profit/loss monitoring thresholds
                    </span>
                  </button>
                  
                  {showPnLThresholds && (
                    <div className="p-4 border-t border-border">
                      <div className="bg-muted/30 border border-border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h6 className="text-sm font-medium text-foreground flex items-center">
                            <span className="w-2 h-2 bg-yellow-500 rounded-full mr-2"></span>
                            💰 PnL Threshold Monitoring
                          </h6>
                          <Checkbox
                            id="edit-pnl_enabled"
                            checked={formData.pnl_enabled}
                            onCheckedChange={(checked) => setFormData(prev => ({ ...prev, pnl_enabled: !!checked }))}
                          />
                        </div>
                        <p className="text-xs text-muted-foreground mb-3">
                          Trigger LLM consultation when position profit/loss changes significantly
                        </p>
                        
                        {formData.pnl_enabled && (
                          <div>
                            <h6 className="text-sm font-medium text-foreground mb-3 flex items-center">
                              <span className="w-2 h-2 bg-yellow-500 rounded-full mr-2"></span>
                              ⚙️ Threshold Configuration
                            </h6>
                            
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div>
                                <label className="block text-xs font-medium text-foreground mb-1">
                                  📈 Profit Threshold (%)
                                </label>
                                <input
                                  type="number"
                                  name="pnl_profit_threshold"
                                  min="0.1"
                                  max="20"
                                  step="0.1"
                                  value={formData.pnl_profit_threshold}
                                  onChange={handleInputChange}
                                  className="w-full px-2 py-1 text-sm border border-input bg-background text-foreground rounded focus:ring-1 focus:ring-primary focus:border-primary"
                                />
                                <p className="text-xs text-muted-foreground mt-1">
                                  Consult when profit changes by this % (min: 0.1%)
                                </p>
                              </div>
                              
                              <div>
                                <label className="block text-xs font-medium text-foreground mb-1">
                                  📉 Loss Threshold (%)
                                </label>
                                <input
                                  type="number"
                                  name="pnl_loss_threshold"
                                  min="0.1"
                                  max="20"
                                  step="0.1"
                                  value={formData.pnl_loss_threshold}
                                  onChange={handleInputChange}
                                  className="w-full px-2 py-1 text-sm border border-input bg-background text-foreground rounded focus:ring-1 focus:ring-primary focus:border-primary"
                                />
                                <p className="text-xs text-muted-foreground mt-1">
                                  Consult when loss changes by this % (min: 0.1%)
                                </p>
                              </div>
                              
                              <div>
                                <label className="block text-xs font-medium text-foreground mb-1">
                                  ⏱️ Cooldown (minutes)
                                </label>
                                <input
                                  type="number"
                                  name="pnl_cooldown_minutes"
                                  min="5"
                                  max="60"
                                  step="5"
                                  value={formData.pnl_cooldown_minutes}
                                  onChange={handleInputChange}
                                  className="w-full px-2 py-1 text-sm border border-input bg-background text-foreground rounded focus:ring-1 focus:ring-primary focus:border-primary"
                                />
                                <p className="text-xs text-muted-foreground mt-1">
                                  Minimum time between PnL consultations
                                </p>
                              </div>
                            </div>
                            
                            <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                              <div className="flex items-start">
                                <div className="flex-shrink-0">
                                  <svg className="h-4 w-4 text-yellow-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                </div>
                                <div className="ml-2">
                                  <p className="text-xs text-yellow-700 dark:text-yellow-300">
                                    <strong>How it works:</strong> When a position's PnL changes by the configured threshold 
                                    (e.g., profit goes from 2% to 7%, exceeding the 5% threshold), the bot will consult the LLM 
                                    for guidance even if market conditions haven't changed significantly. This helps capture 
                                    profit-taking or stop-loss opportunities based on position performance.
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Trend Analyzer Settings */}
              <div className="mb-6 border border-border rounded-lg">
                <button
                  type="button"
                  onClick={() => setShowTrendAnalyzer(!showTrendAnalyzer)}
                  className={`flex items-center justify-between w-full text-left p-4 ${isEdit ? 'bg-muted/50 hover:bg-muted' : 'bg-muted hover:bg-muted/80'} rounded-t-lg transition-colors`}
                >
                  <h5 className="text-sm font-semibold text-foreground flex items-center">
                    <svg className={`w-4 h-4 mr-2 transition-transform ${showTrendAnalyzer ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    <span className="w-3 h-3 bg-green-500 rounded-full mr-2"></span>
                    Trend Analyzer Settings
                  </h5>
                  <span className="text-xs text-muted-foreground">
                    {showTrendAnalyzer ? 'Hide' : 'Show'} trend validation parameters
                  </span>
                </button>
                
                {showTrendAnalyzer && (
                  <div className="p-4 border-t border-border">
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Trend Analysis Settings */}
                  <div className={`${isEdit ? 'bg-muted/30' : 'bg-muted'} border border-border rounded-lg p-4`}>
                    <h6 className="font-medium text-foreground mb-3 flex items-center">
                      <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                      Trend Analysis
                    </h6>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-foreground mb-1">Trend Threshold</label>
                        <input
                          type="number"
                          name="trend_threshold"
                          min="10"
                          max="50"
                          step="1"
                          value={formData.trend_threshold}
                          onChange={handleInputChange}
                          className="w-full px-2 py-1 text-sm border border-input bg-background text-foreground rounded focus:ring-1 focus:ring-primary focus:border-primary"
                        />
                        <p className="text-xs text-muted-foreground mt-1">ADX threshold for trend strength (default: 25)</p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-foreground mb-1">Volume Threshold</label>
                        <input
                          type="number"
                          name="volume_threshold"
                          min="0.1"
                          max="2.0"
                          step="0.1"
                          value={formData.volume_threshold}
                          onChange={handleInputChange}
                          className="w-full px-2 py-1 text-sm border border-input bg-background text-foreground rounded focus:ring-1 focus:ring-primary focus:border-primary"
                        />
                        <p className="text-xs text-muted-foreground mt-1">Volume confirmation threshold (0.0-1.0)</p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-foreground mb-1">Price Action Threshold</label>
                        <input
                          type="number"
                          name="price_action_threshold"
                          min="0.1"
                          max="1.0"
                          step="0.01"
                          value={formData.price_action_threshold}
                          onChange={handleInputChange}
                          className="w-full px-2 py-1 text-sm border border-input bg-background text-foreground rounded focus:ring-1 focus:ring-primary focus:border-primary"
                        />
                        <p className="text-xs text-muted-foreground mt-1">Price action confidence threshold</p>
                      </div>
                    </div>
                  </div>

                  {/* Market Structure Settings */}
                  <div className={`${isEdit ? 'bg-muted/30' : 'bg-muted'} border border-border rounded-lg p-4`}>
                    <h6 className="font-medium text-foreground mb-3 flex items-center">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                      Market Structure
                    </h6>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-foreground mb-1">Swing Window</label>
                        <input
                          type="number"
                          name="swing_window"
                          min="3"
                          max="20"
                          step="1"
                          value={formData.swing_window}
                          onChange={handleInputChange}
                          className="w-full px-2 py-1 text-sm border border-input bg-background text-foreground rounded focus:ring-1 focus:ring-primary focus:border-primary"
                        />
                        <p className="text-xs text-muted-foreground mt-1">Window size for swing high/low detection</p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-foreground mb-1">Divergence Periods</label>
                        <input
                          type="number"
                          name="divergence_periods"
                          min="3"
                          max="15"
                          step="1"
                          value={formData.divergence_periods}
                          onChange={handleInputChange}
                          className="w-full px-2 py-1 text-sm border border-input bg-background text-foreground rounded focus:ring-1 focus:ring-primary focus:border-primary"
                        />
                        <p className="text-xs text-muted-foreground mt-1">Periods for divergence analysis</p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-foreground mb-1">Price Action Lookback</label>
                        <input
                          type="number"
                          name="price_action_lookback"
                          min="5"
                          max="30"
                          step="1"
                          value={formData.price_action_lookback}
                          onChange={handleInputChange}
                          className="w-full px-2 py-1 text-sm border border-input bg-background text-foreground rounded focus:ring-1 focus:ring-primary focus:border-primary"
                        />
                        <p className="text-xs text-muted-foreground mt-1">Lookback periods for price action analysis</p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-foreground mb-1">Overall Confidence Threshold</label>
                        <input
                          type="number"
                          name="overall_confidence_threshold"
                          min="0.1"
                          max="1.0"
                          step="0.05"
                          value={formData.overall_confidence_threshold}
                          onChange={handleInputChange}
                          className="w-full px-2 py-1 text-sm border border-input bg-background text-foreground rounded focus:ring-1 focus:ring-primary focus:border-primary"
                        />
                        <p className="text-xs text-muted-foreground mt-1">Minimum overall confidence for trend validation (0.0-1.0)</p>
                      </div>
                    </div>
                  </div>
                </div>
                  </div>
                )}
              </div>
              
              {/* Analysis Settings */}
              <div className="mb-6 border border-border rounded-lg">
                <button
                  type="button"
                  onClick={() => setShowAnalysisSettings(!showAnalysisSettings)}
                  className="flex items-center justify-between w-full text-left p-4 bg-muted hover:bg-muted/80 rounded-t-lg transition-colors"
                >
                  <h5 className="text-sm font-semibold text-foreground flex items-center">
                    <svg className={`w-4 h-4 mr-2 transition-transform ${showAnalysisSettings ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    <span className="w-3 h-3 bg-red-500 rounded-full mr-2"></span>
                    Analysis Settings
                  </h5>
                  <span className="text-xs text-muted-foreground">
                    {showAnalysisSettings ? 'Hide' : 'Show'} configurable analysis steps
                  </span>
                </button>
                
                {showAnalysisSettings && (
                  <div className="p-4 border-t border-border">
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-6">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">Trading Loop Configuration</h3>
                          <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-300">
                            <p>Control which analysis steps are applied in the trading loop:</p>
                            <p className="mt-1 font-medium">Technical Indicators → Risk Analysis → Trend Analysis → Volume Analysis → LLM Decision</p>
                            <p className="mt-1">Disabling steps allows more trading opportunities to reach the LLM but with potentially higher risk.</p>
                          </div>
                        </div>
                      </div>
                    </div>
                
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      {/* Risk Analysis */}
                      <div className="bg-muted border border-border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h6 className="font-medium text-foreground flex items-center">
                            <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                            🛡️ Risk Analysis
                          </h6>
                          <Checkbox
                            id={`${idPrefix}enable_risk_analysis`}
                            checked={formData.enable_risk_analysis}
                            onCheckedChange={(checked) => setFormData(prev => ({ ...prev, enable_risk_analysis: !!checked }))}
                          />
                        </div>
                        <p className="text-xs text-muted-foreground mb-2">
                          Filters high-risk pairs based on volatility, ATR, and risk indicators.
                        </p>
                        {formData.enable_risk_analysis && (
                          <div className="mt-3 p-2 bg-background/50 rounded border border-border/50">
                            <label className="flex items-center text-xs">
                              <Checkbox
                                id={`${idPrefix}auto_configure_risk`}
                                checked={formData.auto_configure_risk}
                                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, auto_configure_risk: !!checked }))}
                                className="mr-2"
                              />
                              <span className="font-medium">🤖 Auto-configure with LLM</span>
                            </label>
                            <p className="text-xs text-muted-foreground mt-1 ml-6">
                              Let AI dynamically adjust risk thresholds based on market conditions
                            </p>
                          </div>
                        )}
                        <div className="text-xs mt-2">
                          <div className="flex items-center text-muted-foreground">
                            <span className="w-1 h-1 bg-muted-foreground rounded-full mr-1"></span>
                            Removes pairs with extreme volatility
                          </div>
                          <div className="flex items-center text-muted-foreground">
                            <span className="w-1 h-1 bg-muted-foreground rounded-full mr-1"></span>
                            Checks ATR and MACD extremes
                          </div>
                          <div className="flex items-center text-muted-foreground">
                            <span className="w-1 h-1 bg-muted-foreground rounded-full mr-1"></span>
                            Prevents high-risk trades
                          </div>
                        </div>
                      </div>

                      {/* Trend Analysis */}
                      <div className="bg-muted border border-border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h6 className="font-medium text-foreground flex items-center">
                            <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                            📈 Trend Analysis
                          </h6>
                          <Checkbox
                            id={`${idPrefix}enable_trend_analysis`}
                            checked={formData.enable_trend_analysis}
                            onCheckedChange={(checked) => setFormData(prev => ({ ...prev, enable_trend_analysis: !!checked }))}
                          />
                        </div>
                        <p className="text-xs text-muted-foreground mb-2">
                          Validates trend strength using ADX and trend consistency across timeframes.
                        </p>
                        {formData.enable_trend_analysis && (
                          <div className="mt-3 p-2 bg-background/50 rounded border border-border/50">
                            <label className="flex items-center text-xs">
                              <Checkbox
                                id={`${idPrefix}auto_configure_trend`}
                                checked={formData.auto_configure_trend}
                                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, auto_configure_trend: !!checked }))}
                                className="mr-2"
                              />
                              <span className="font-medium">🤖 Auto-configure with LLM</span>
                            </label>
                            <p className="text-xs text-muted-foreground mt-1 ml-6">
                              AI adjusts trend parameters for market conditions
                            </p>
                          </div>
                        )}
                        <div className="text-xs mt-2">
                          <div className="flex items-center text-muted-foreground">
                            <span className="w-1 h-1 bg-muted-foreground rounded-full mr-1"></span>
                            Checks ADX trend strength
                          </div>
                          <div className="flex items-center text-muted-foreground">
                            <span className="w-1 h-1 bg-muted-foreground rounded-full mr-1"></span>
                            Multi-timeframe validation
                          </div>
                          <div className="flex items-center text-muted-foreground">
                            <span className="w-1 h-1 bg-muted-foreground rounded-full mr-1"></span>
                            Ensures trend consistency
                          </div>
                        </div>
                      </div>

                      {/* Volume Analysis */}
                      <div className="bg-muted border border-border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h6 className="font-medium text-foreground flex items-center">
                            <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                            📊 Volume Analysis
                          </h6>
                          <Checkbox
                            id={`${idPrefix}enable_volume_analysis`}
                            checked={formData.enable_volume_analysis}
                            onCheckedChange={(checked) => setFormData(prev => ({ ...prev, enable_volume_analysis: !!checked }))}
                          />
                        </div>
                        <p className="text-xs text-muted-foreground mb-2">
                          Confirms price movements with volume analysis and price-volume correlation.
                        </p>
                        {formData.enable_volume_analysis && (
                          <div className="mt-3 p-2 bg-background/50 rounded border border-border/50">
                            <label className="flex items-center text-xs">
                              <Checkbox
                                id={`${idPrefix}auto_configure_volume`}
                                checked={formData.auto_configure_volume}
                                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, auto_configure_volume: !!checked }))}
                                className="mr-2"
                              />
                              <span className="font-medium">🤖 Auto-configure with LLM</span>
                            </label>
                            <p className="text-xs text-muted-foreground mt-1 ml-6">
                              AI optimizes volume thresholds dynamically
                            </p>
                          </div>
                        )}
                        <div className="text-xs mt-2">
                          <div className="flex items-center text-muted-foreground">
                            <span className="w-1 h-1 bg-muted-foreground rounded-full mr-1"></span>
                            Volume confirmation checks
                          </div>
                          <div className="flex items-center text-muted-foreground">
                            <span className="w-1 h-1 bg-muted-foreground rounded-full mr-1"></span>
                            Price-volume correlation
                          </div>
                          <div className="flex items-center text-muted-foreground">
                            <span className="w-1 h-1 bg-muted-foreground rounded-full mr-1"></span>
                            Validates trade signals
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Auto-configuration Settings */}
                    {(formData.auto_configure_risk || formData.auto_configure_trend || formData.auto_configure_volume) && (
                      <div className="mt-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                        <h6 className="font-medium text-blue-800 dark:text-blue-200 mb-3 flex items-center">
                          <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                          🤖 Auto-Configuration Settings
                        </h6>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="flex items-center text-xs">
                              <Checkbox
                                id={`${idPrefix}reconfigure_on_position_close`}
                                checked={formData.reconfigure_on_position_close}
                                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, reconfigure_on_position_close: !!checked }))}
                                className="mr-2"
                              />
                              <span className="font-medium text-blue-700 dark:text-blue-300">Reconfigure on Position Close</span>
                            </label>
                            <p className="text-xs text-blue-600 dark:text-blue-400 mt-1 ml-6">
                              Update analysis parameters after closing positions
                            </p>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-blue-700 dark:text-blue-300 mb-1">
                              Reconfiguration Interval (hours)
                            </label>
                            <input
                              type="number"
                              name="reconfigure_interval_hours"
                              min="1"
                              max="168"
                              step="1"
                              value={formData.reconfigure_interval_hours}
                              onChange={handleInputChange}
                              className="w-full px-2 py-1 text-sm border border-blue-200 dark:border-blue-700 bg-white dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 rounded focus:ring-1 focus:ring-blue-400"
                            />
                            <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                              Periodic reconfiguration interval
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                      <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
                        <div className="text-sm font-medium text-green-800 dark:text-green-200">Conservative</div>
                        <div className="text-xs text-green-600 dark:text-green-400 mt-1">All analysis enabled</div>
                        <div className="text-xs text-muted-foreground mt-1">Fewer opportunities, higher quality</div>
                      </div>
                      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
                        <div className="text-sm font-medium text-yellow-800 dark:text-yellow-200">Moderate</div>
                        <div className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">Risk + Trend enabled</div>
                        <div className="text-xs text-muted-foreground mt-1">Balanced opportunities vs risk</div>
                      </div>
                      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                        <div className="text-sm font-medium text-red-800 dark:text-red-200">Aggressive</div>
                        <div className="text-xs text-red-600 dark:text-red-400 mt-1">Only risk analysis</div>
                        <div className="text-xs text-muted-foreground mt-1">More opportunities, higher risk</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-blue-800">About Advanced Settings</h3>
                    <div className="mt-2 text-sm text-blue-700 dark:text-blue-300">
                      <p>These settings control how the bot interprets technical indicators and validates trading opportunities:</p>
                      <ul className="mt-2 list-disc list-inside space-y-1">
                        <li><strong>Analysis Settings:</strong> Enable/disable risk, trend, and volume analysis steps</li>
                        <li><strong>Technical Thresholds:</strong> Determine overbought/oversold conditions and signal strength</li>
                        <li><strong>Trend Analysis:</strong> Configure trend validation parameters and confidence levels</li>
                        <li><strong>Market Structure:</strong> Control swing detection and divergence analysis settings</li>
                      </ul>
                      <p className="mt-2 font-medium">These advanced settings give you full control over the bot's decision-making process and trading loop behavior.</p>
                    </div>
                  </div>
                </div>
              </div>
              </div>
              )}
            </div>

            {/* API Keys Section */}
            <div className="border-t pt-6">
              <button
                type="button"
                onClick={() => setShowApiKeys(!showApiKeys)}
                className={`flex items-center justify-between w-full text-left mb-4 p-3 ${isEdit ? 'bg-muted/50 hover:bg-muted' : 'bg-muted hover:bg-muted/80'} rounded-lg transition-colors`}
              >
                <h4 className="text-md font-medium text-foreground flex items-center">
                  <svg className={`w-5 h-5 mr-2 transition-transform ${showApiKeys ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  API Keys
                </h4>
                <span className="text-sm text-muted-foreground">
                  {showApiKeys ? 'Hide' : 'Show'} API configuration
                </span>
              </button>
              
              {showApiKeys && (
              <div className="space-y-6">
                {/* Binance API Keys */}
                <div className="bg-muted/30 border border-border rounded-lg p-4">
                  <h5 className="text-sm font-semibold text-foreground mb-4 flex items-center">
                    <span className="w-3 h-3 bg-yellow-500 rounded-full mr-2"></span>
                    Binance API Keys <span className="text-red-500 ml-1">*</span>
                  </h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {formData.testnet ? (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-foreground">
                            Binance Testnet API Key <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="binance_testnet_api_key"
                            required
                            value={formData.binance_testnet_api_key}
                            onChange={handleInputChange}
                            className="mt-1 block w-full border-input bg-background text-foreground rounded-md shadow-sm focus:ring-primary focus:border-primary"
                            placeholder="Enter your Binance Testnet API Key"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-foreground">
                            Binance Testnet Secret <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="password"
                            name="binance_testnet_secret_key"
                            required
                            value={formData.binance_testnet_secret_key}
                            onChange={handleInputChange}
                            className="mt-1 block w-full border-input bg-background text-foreground rounded-md shadow-sm focus:ring-primary focus:border-primary"
                            placeholder="Enter your Binance Testnet Secret"
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-foreground">
                            Binance API Key <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="binance_api_key"
                            required
                            value={formData.binance_api_key}
                            onChange={handleInputChange}
                            className="mt-1 block w-full border-input bg-background text-foreground rounded-md shadow-sm focus:ring-primary focus:border-primary"
                            placeholder="Enter your Binance API Key"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-foreground">
                            Binance Secret <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="password"
                            name="binance_secret_key"
                            required
                            value={formData.binance_secret_key}
                            onChange={handleInputChange}
                            className="mt-1 block w-full border-input bg-background text-foreground rounded-md shadow-sm focus:ring-primary focus:border-primary"
                            placeholder="Enter your Binance Secret"
                          />
                        </div>
                      </>
                    )}
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">
                    Required for trading operations. {formData.testnet ? 'Using Binance Testnet for safe testing.' : 'Using live Binance trading.'}
                  </p>
                </div>

                {/* Gemini API Key */}
                <div className="bg-muted/30 border border-border rounded-lg p-4">
                  <h5 className="text-sm font-semibold text-foreground mb-4 flex items-center">
                    <span className="w-3 h-3 bg-blue-500 rounded-full mr-2"></span>
                    Gemini API Key <span className="text-red-500 ml-1">*</span>
                  </h5>
                  <div>
                    <label className="block text-sm font-medium text-foreground">
                      Gemini API Key <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="gemini_api_key"
                      required
                      value={formData.gemini_api_key}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border-input bg-background text-foreground rounded-md shadow-sm focus:ring-primary focus:border-primary"
                      placeholder="Enter your Gemini API Key"
                    />
                    <p className="mt-1 text-xs text-muted-foreground">Required for LLM trading analysis.</p>
                  </div>
                </div>

                {/* CryptoPanic API Key - Conditional */}
                {formData.news_fetcher && (
                  <div className="bg-muted/30 border border-border rounded-lg p-4">
                    <h5 className="text-sm font-semibold text-foreground mb-4 flex items-center">
                      <span className="w-3 h-3 bg-green-500 rounded-full mr-2"></span>
                      CryptoPanic API Key <span className="text-red-500 ml-1">*</span>
                    </h5>
                    <div>
                      <label className="block text-sm font-medium text-foreground">
                        CryptoPanic API Key <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="cryptopanic_api_key"
                        required={formData.news_fetcher}
                        value={formData.cryptopanic_api_key}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border-input bg-background text-foreground rounded-md shadow-sm focus:ring-primary focus:border-primary"
                        placeholder="Enter your CryptoPanic API Key"
                      />
                      <p className="mt-1 text-xs text-muted-foreground">Required when News Fetching is enabled.</p>
                    </div>
                  </div>
                )}

                {/* Telegram API Keys - Conditional */}
                {formData.telegram_notifications && (
                  <div className="bg-muted/30 border border-border rounded-lg p-4">
                    <h5 className="text-sm font-semibold text-foreground mb-4 flex items-center">
                      <span className="w-3 h-3 bg-purple-500 rounded-full mr-2"></span>
                      Telegram API Keys <span className="text-red-500 ml-1">*</span>
                    </h5>
                    
                    {/* Basic Telegram Settings */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      <div>
                        <label className="block text-sm font-medium text-foreground">
                          Telegram Bot Token <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="telegram_bot_token"
                          required={formData.telegram_notifications}
                          value={formData.telegram_bot_token}
                          onChange={handleInputChange}
                          className="mt-1 block w-full border-input bg-background text-foreground rounded-md shadow-sm focus:ring-primary focus:border-primary"
                          placeholder="Get from @BotFather on Telegram"
                        />
                        <p className="mt-1 text-xs text-muted-foreground">Get from @BotFather on Telegram.</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground">
                          Telegram Chat ID <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="telegram_chat_id"
                          required={formData.telegram_notifications}
                          value={formData.telegram_chat_id}
                          onChange={handleInputChange}
                          className="mt-1 block w-full border-input bg-background text-foreground rounded-md shadow-sm focus:ring-primary focus:border-primary"
                          placeholder="Your chat ID for notifications"
                        />
                        <p className="mt-1 text-xs text-muted-foreground">Your chat ID to receive notifications.</p>
                      </div>
                    </div>

                    <p className="mt-2 text-xs text-muted-foreground">
                      Required for Telegram notifications. Configure notification preferences in Advanced Settings below.
                    </p>
                  </div>
                )}

              </div>
              )}
            </div>

            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded">
                {error}
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-6 border-t border-border">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-input rounded-md text-sm font-medium text-foreground bg-background hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
              >
                {loading ? loadingText : submitText}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BotFormModal;